import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import {
  IAcceptPartProTradeBid,
  ICancelPartProTradeAward,
  IDecidePartProTradeLot,
  IPartProTradeBidsPage,
  IPartProTradeBidVehicleDetail,
  IRejectPartProTradeVehicle,
  PartProBidsTab,
} from "../models/partPro/IPartProTradeBids";
import apiConfig from "../utils/apiConfig";

const BASE_URL = "/partpro/trade/vehicles";
const BIDS_KEY = ["partpro", "trade", "bids"];

type DetailResponse = DefaultResponseWithData<IPartProTradeBidVehicleDetail>;

/** One page of a Bids tab - PartPro pages, searches and filters it, Core keeps no copy. */
export function usePartProTradeBidsPage(tab: PartProBidsTab, query: string, page: number, pageSize: number) {
  const { get } = useApiHelper(apiConfig("PartProBids"));

  return useQuery({
    queryKey: [...BIDS_KEY, "list", tab, query, page, pageSize],
    queryFn: () =>
      get<DefaultResponseWithData<IPartProTradeBidsPage>>(
        `${BASE_URL}?tab=${tab}&q=${encodeURIComponent(query)}&page=${page}&pageSize=${pageSize}`
      ),
    keepPreviousData: true,
    // A 400 here means "not enabled" or "not connected" - retrying will not change that
    retry: false,
  });
}

/** The bids on a Core vehicle, for its pricing matrix. No data when it isn't on PartPro Trade. */
export function usePartProTradeCoreVehicleBids(coreVehicleId?: string) {
  const { get } = useApiHelper(apiConfig("VehiclePricingMatrix"));

  return useQuery({
    queryKey: [...BIDS_KEY, "core", coreVehicleId],
    queryFn: () => get<DetailResponse>(`${BASE_URL}/core/${coreVehicleId}`),
    enabled: !!coreVehicleId,
    retry: false,
  });
}

/**
 * Accept bids from the pricing matrix: ends bidding first if it is still open, awards each pick
 * and writes the winning amounts into the matrix.
 */
export function useAcceptPartProTradeBids(coreVehicleId?: string) {
  const { post } = useApiHelper(apiConfig("VehiclePricingMatrix"));
  const queryClient = useQueryClient();

  // Refreshed on failure too - a refusal usually means the bids moved underneath us
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: BIDS_KEY });
    queryClient.invalidateQueries({ queryKey: ["vehicles", coreVehicleId, "pricing-matrix"] });
  };

  return useMutation({
    mutationFn: ({ tradeVehicleId, bids }: { tradeVehicleId: string; bids: IAcceptPartProTradeBid[] }) =>
      post<{ bids: IAcceptPartProTradeBid[] }, DetailResponse>(`${BASE_URL}/${tradeVehicleId}/accept`, { bids }),
    onSuccess: refresh,
    onError: refresh,
  });
}

/**
 * Cancel an accepted bid from the pricing matrix. PartPro emails the buyer the reason, the
 * category is closed for good and the matrix cell is cleared and unlocked.
 */
export function useCancelPartProTradeAward(coreVehicleId?: string) {
  const { post } = useApiHelper(apiConfig("VehiclePricingMatrix"));
  const queryClient = useQueryClient();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: BIDS_KEY });
    queryClient.invalidateQueries({ queryKey: ["vehicles", coreVehicleId, "pricing-matrix"] });
  };

  return useMutation({
    mutationFn: ({ tradeVehicleId, lotId, ...body }: ICancelPartProTradeAward & { tradeVehicleId: string; lotId: string }) =>
      post<ICancelPartProTradeAward, DetailResponse>(`${BASE_URL}/${tradeVehicleId}/lots/${lotId}/cancel`, body),
    onSuccess: refresh,
    onError: refresh,
  });
}

/**
 * Reject the whole vehicle from the pricing matrix - it didn't turn up, or wasn't as described.
 * Every accepted bid is cancelled and its matrix cell cleared.
 */
export function useRejectPartProTradeVehicle(coreVehicleId?: string) {
  const { post } = useApiHelper(apiConfig("VehiclePricingMatrix"));
  const queryClient = useQueryClient();

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: BIDS_KEY });
    queryClient.invalidateQueries({ queryKey: ["vehicles", coreVehicleId, "pricing-matrix"] });
  };

  return useMutation({
    mutationFn: ({ tradeVehicleId, ...body }: IRejectPartProTradeVehicle & { tradeVehicleId: string }) =>
      post<IRejectPartProTradeVehicle, DetailResponse>(`${BASE_URL}/${tradeVehicleId}/reject`, body),
    onSuccess: refresh,
    onError: refresh,
  });
}

export function usePartProTradeBidVehicle(vehicleId?: string) {
  const { get, post, del } = useApiHelper(apiConfig("PartProBidVehicle"));
  const queryClient = useQueryClient();
  const queryKey = [...BIDS_KEY, "vehicle", vehicleId];

  const vehicle = useQuery({
    queryKey,
    queryFn: () => get<DetailResponse>(`${BASE_URL}/${vehicleId}`),
    enabled: !!vehicleId,
    retry: false,
  });

  // Every action answers with the vehicle as PartPro now holds it
  const onSuccess = (res: DetailResponse) => {
    queryClient.setQueryData(queryKey, res);
    queryClient.invalidateQueries({ queryKey: [...BIDS_KEY, "list"] });
    queryClient.invalidateQueries({ queryKey: [...BIDS_KEY, "core"] });
  };

  const decideLot = useMutation({
    mutationFn: ({ lotId, ...body }: IDecidePartProTradeLot & { lotId: string }) =>
      post<IDecidePartProTradeLot, DetailResponse>(`${BASE_URL}/${vehicleId}/lots/${lotId}/decision`, body),
    onSuccess,
  });

  const reopenLot = useMutation({
    mutationFn: (lotId: string) => del<DetailResponse>(`${BASE_URL}/${vehicleId}/lots/${lotId}/decision`),
    onSuccess,
  });

  const endBidding = useMutation({
    mutationFn: () => post<object, DetailResponse>(`${BASE_URL}/${vehicleId}/close`, {}),
    onSuccess,
  });

  const setDeadline = useMutation({
    mutationFn: (biddingEndsAt: string) =>
      post<{ biddingEndsAt: string }, DetailResponse>(`${BASE_URL}/${vehicleId}/deadline`, { biddingEndsAt }),
    onSuccess,
  });

  const cancelAward = useMutation({
    mutationFn: ({ lotId, ...body }: ICancelPartProTradeAward & { lotId: string }) =>
      post<ICancelPartProTradeAward, DetailResponse>(`${BASE_URL}/${vehicleId}/lots/${lotId}/cancel`, body),
    onSuccess: (res: DetailResponse) => {
      onSuccess(res);
      // The cancelled bid's matrix cell has been cleared
      if (res.data?.coreVehicleId)
        queryClient.invalidateQueries({ queryKey: ["vehicles", res.data.coreVehicleId, "pricing-matrix"] });
    },
  });

  const rejectVehicle = useMutation({
    mutationFn: (body: IRejectPartProTradeVehicle) =>
      post<IRejectPartProTradeVehicle, DetailResponse>(`${BASE_URL}/${vehicleId}/reject`, body),
    onSuccess: (res: DetailResponse) => {
      onSuccess(res);
      // Every accepted bid's matrix cell has been cleared
      if (res.data?.coreVehicleId)
        queryClient.invalidateQueries({ queryKey: ["vehicles", res.data.coreVehicleId, "pricing-matrix"] });
    },
  });

  return { vehicle, decideLot, reopenLot, endBidding, setDeadline, cancelAward, rejectVehicle };
}
